/**
 * DraftStageProgress — 5-step drafting progress with live ETA.
 *
 * Replaces the static `<DraftLoadingPlaceholder>` spinner with a
 * Sequencer-style visualisation of the actual pipeline stages.
 *
 * Audit-2026-05-12 Bug #3 (Option C): the backend orchestrator now
 * publishes per-stage `stage_progress` snapshots to the draft_jobs
 * row; the frontend polling loop forwards them here.
 *
 * Visual idiom matches `components/draft/SequencerPanel.jsx` so the
 * user already understands the language from the review/compliance
 * flow.
 *
 * Props:
 *   progress: {
 *     current_stage: string,
 *     stage_index: 1..5,
 *     total_stages: 5,
 *     started_at: ISO,
 *     eta_seconds: number,           // ETA for THIS stage
 *     remaining_eta_seconds: number, // ETA for THIS stage → done
 *   } | null
 *   startedAt: number   // Date.now() of overall flow start (fallback timer)
 *   isArabic: boolean
 *   isComplete: boolean // true when status === 'done' (light up stage 5)
 *   isFailed:   boolean // true when status === 'failed' (red dot)
 */
import React, { useEffect, useState } from 'react';
import { MorphLoading } from '@/components/ui/MorphLoading';

const STAGE_LABELS = [
  { key: 'stage1_initial_generation',  en: 'Initial Draft',   ar: 'الصياغة الأولية' },
  { key: 'stage2_quality_review',      en: 'Quality Review',  ar: 'مراجعة الجودة' },
  { key: 'stage3_improvement',         en: 'Improvement',     ar: 'التحسين' },
  { key: 'stage4_final_polish',        en: 'Final Polish',    ar: 'الصقل النهائي' },
  { key: 'completed',                  en: 'Done',            ar: 'اكتمل' },
];

function statusDot(state) {
  if (state === 'done')        return { bg: '#1F5A2D', glyph: '✓' };
  if (state === 'in_progress') return { bg: '#1F1F1F', glyph: '●' };
  if (state === 'failed')      return { bg: '#911',    glyph: '✗' };
  return                             { bg: '#bbb',    glyph: '○' };
}

function formatMmSs(totalSeconds) {
  const total = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function DraftStageProgress({
  progress, startedAt, isArabic, isComplete = false, isFailed = false,
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const activeIndex = isComplete ? 5 : (progress?.stage_index || 1);

  // Per-stage elapsed timer (still used for the small label next to the
  // active row). The high-level mm:ss ETA countdown was replaced by a
  // morphing loader because long gpt-5.5 runs (~2-3 min) made the
  // numeric timer feel pessimistic.
  const stageStartedAtMs = (() => {
    if (!progress?.started_at) return startedAt || now;
    const parsed = Date.parse(progress.started_at);
    return Number.isFinite(parsed) ? parsed : (startedAt || now);
  })();
  const elapsedInStageSec = Math.max(0, (now - stageStartedAtMs) / 1000);

  const statusLabel = (() => {
    if (isComplete) {
      return isArabic ? 'اكتملت المسودة' : 'Draft complete';
    }
    if (isFailed) {
      return isArabic ? 'تعذّر إكمال المسودة' : 'Draft could not complete';
    }
    return isArabic ? 'نعمل على ذلك...' : 'Working on it...';
  })();

  // Overall elapsed shown subtly below the status copy so power users
  // still see how long the run is taking.
  const overallElapsedSec = Math.max(0, Math.floor((now - (startedAt || now)) / 1000));

  return (
    <div
      className="h-full w-full flex items-start justify-center px-8 py-12"
      data-testid="draft-stage-progress"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div className="max-w-xl w-full flex flex-col gap-6">

        <div className="flex flex-col items-center gap-3 text-center">
          {!isComplete && !isFailed && (
            <MorphLoading size="lg" data-testid="draft-progress-morph" />
          )}
          <div
            className="text-base"
            style={{ color: '#111', fontWeight: 500 }}
            data-testid="draft-progress-eta"
          >
            {statusLabel}
          </div>
          <div className="text-xs" style={{ color: '#999' }} data-testid="draft-progress-elapsed">
            {isArabic ? 'الزمن المنقضي' : 'Elapsed'}: {formatMmSs(overallElapsedSec)}
          </div>
        </div>

        <div
          className="flex flex-col gap-3"
          style={{
            background: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: 8,
            padding: 18,
          }}
        >
          {STAGE_LABELS.map((stage, i) => {
            const idx = i + 1;
            let state = 'waiting';
            if (isFailed && idx === activeIndex) state = 'failed';
            else if (idx < activeIndex) state = 'done';
            else if (idx === activeIndex) state = isComplete ? 'done' : 'in_progress';
            const dot = statusDot(state);
            const label = isArabic ? stage.ar : stage.en;
            const showTimer = state === 'in_progress' && !isComplete && !isFailed;
            return (
              <div
                key={stage.key}
                className="flex items-center"
                style={{ gap: 12 }}
                data-testid={`draft-stage-row-${idx}`}
                data-state={state}
              >
                <div
                  style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: dot.bg, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 500, flexShrink: 0,
                  }}
                  aria-label={state}
                >
                  {dot.glyph}
                </div>
                <div className="flex-1 text-sm" style={{ color: state === 'waiting' ? '#999' : '#111', fontWeight: state === 'in_progress' ? 500 : 400 }}>
                  {label}
                </div>
                {showTimer && (
                  <div className="text-xs" style={{ color: '#666' }} data-testid={`draft-stage-row-${idx}-timer`}>
                    {formatMmSs(elapsedInStageSec)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DraftStageProgress;
